import type { Catalog } from '@arm-pd/domain';
import { Loading } from './Loading';
import { ErrorMessage, EmptyState } from './ErrorMessage';

interface CatalogSelectorProps {
  catalogs: Catalog[] | undefined;
  selectedCatalogId: string | undefined;
  onSelect: (catalogId: string) => void;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
}

/**
 * Catalog selector component for choosing a product catalog
 * Displays catalogs in a card grid format
 */
export function CatalogSelector({
  catalogs,
  selectedCatalogId,
  onSelect,
  isLoading,
  error,
  onRetry,
}: CatalogSelectorProps) {
  const onRetryFn = onRetry ?? (() => {});
  if (isLoading) {
    return (
      <div className="py-8">
        <h2 className="mb-4 text-lg font-medium text-gray-900">Select Catalog</h2>
        <Loading message="Loading catalogs..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <h2 className="mb-4 text-lg font-medium text-gray-900">Select Catalog</h2>
        <ErrorMessage title="Failed to load catalogs" message={error.message} onRetry={onRetryFn} />
      </div>
    );
  }

  if (!catalogs || catalogs.length === 0) {
    return (
      <div className="py-8">
        <h2 className="mb-4 text-lg font-medium text-gray-900">Select Catalog</h2>
        <EmptyState
          title="No catalogs available"
          message="There are no product catalogs to display at this time."
          actionText="Refresh"
          onAction={onRetryFn}
        />
      </div>
    );
  }

  return (
    <div className="py-8">
      <h2 className="mb-4 text-lg font-medium text-gray-900">Select Catalog</h2>
      <p className="mb-6 text-sm text-gray-600">Choose a catalog to browse products</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {catalogs.map(catalog => (
          <button
            key={catalog.id}
            type="button"
            onClick={() => onSelect(catalog.id)}
            className={`
              relative flex flex-col items-start rounded-lg border-2 p-6 text-left transition-all
              ${
                selectedCatalogId === catalog.id
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              touch-min-height
            `}
            aria-pressed={selectedCatalogId === catalog.id}
          >
            {/* Catalog name */}
            <span className="text-lg font-semibold text-gray-900">{catalog.name}</span>

            {/* Catalog code */}
            {catalog.code && (
              <span className="mt-1 text-sm text-gray-500 font-mono">{catalog.code}</span>
            )}

            {/* Category count */}
            {catalog.categoryCount && (
              <span className="mt-2 inline-flex items-center text-sm text-gray-600">
                <svg
                  className="mr-1.5 h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                {catalog.categoryCount} categories
              </span>
            )}

            {/* Selection indicator */}
            {selectedCatalogId === catalog.id && (
              <div className="absolute right-4 top-4">
                <svg
                  className="h-6 w-6 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
